import {Box, Button, Group, Stack} from '@mantine/core';
import React, {useState} from 'react';

import RowByRowSubmissionSetting, {
  RowOnlyConfig,
} from 'notebook/structured-tables/components/RowByRowSubmissionSetting';
import useSelectFolderOptionForTable, {
  StructuredTableFolderConfig,
} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {RunModalProps} from 'ui-library/runModal';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export interface StructuredTableSettingsModalValues {
  rowByRowSubmissionRequired?: boolean;
  folderConfig?: StructuredTableFolderConfig;
}

export interface StructuredTableFolderSettingsConfig {
  initialFolderConfig: StructuredTableFolderConfig;
  itemTypeSupportsFolders: boolean;
  isForCreate: boolean;
  projectIsRequired: boolean;
}

interface StructuredTableSettingsModalProps extends RunModalProps<StructuredTableSettingsModalValues> {
  locked: boolean;
  rowOnlyConfig?: RowOnlyConfig;
  folderSettings?: StructuredTableFolderSettingsConfig;
  children?: React.ReactNode;
  onSave?: (sharedValues: StructuredTableSettingsModalValues) => void;
}

export default function StructuredTableSettingsModal(props: StructuredTableSettingsModalProps): JSX.Element {
  const {showSubmissionSetting, rowOnlyInitialValue = false} = props.rowOnlyConfig ?? {};
  const [rowByRowSubmissionRequired, setRowByRowSubmissionRequired] = useState(rowOnlyInitialValue);

  const folderSettings = props.folderSettings;
  const {selectedFolderConfig, isInvalidFolderConfig, folderOptionSelector} = useSelectFolderOptionForTable({
    initialFolderConfig: folderSettings?.initialFolderConfig,
    itemTypeSupportsFolders: folderSettings?.itemTypeSupportsFolders ?? false,
    isReadOnly: props.locked,
    isForCreate: folderSettings?.isForCreate ?? false,
    projectIsRequired: folderSettings?.projectIsRequired ?? false,
  });

  const isSaveDisabled = props.locked || (folderSettings != null && isInvalidFolderConfig);

  const handleSave = (): void => {
    if (isSaveDisabled) {
      return;
    }
    const sharedValues: StructuredTableSettingsModalValues = {
      ...(folderSettings != null ? {folderConfig: selectedFolderConfig} : {}),
      ...(showSubmissionSetting ? {rowByRowSubmissionRequired} : {}),
    };
    if (props.onSave != null) {
      props.onSave(sharedValues);
      return;
    }
    props.close(sharedValues);
  };

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isOSCtrlKey(e) && keyboard.isEnterKey(e)) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      onKeyDown={handleKeyDown}
    >
      <Stack spacing="sm">
        {props.children}
        {folderSettings != null && folderOptionSelector}
        {showSubmissionSetting && (
          <RowByRowSubmissionSetting
            locked={props.locked}
            rowByRowSubmissionRequired={rowByRowSubmissionRequired}
            onRowByRowSubmissionRequiredChange={setRowByRowSubmissionRequired}
          />
        )}
        <ModalFooter>
          <Group position="right">
            <Button type="submit" disabled={isSaveDisabled}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => props.close()}>
              Cancel
            </Button>
          </Group>
        </ModalFooter>
      </Stack>
    </Box>
  );
}
